import type { ReactNode } from 'react';

import { Tooltip } from '@zen/DesignSystem';
import type { TableColumn } from '@zen/DesignSystem/types';
import { codeToCountryName } from '@zen/utils/countries';
import type { Nullable, Optional } from '@zen/utils/typescript';

import type { WorkflowRuleFieldTableItem, WorkflowRulesListItem } from '../types';

export const workflowRuleTableColumns: Record<string, TableColumn<WorkflowRulesListItem>> = {
  accountId: {
    fixed: 'left',
    key: 'account',
    render: (_, { ruleInput }): ReactNode => {
      return ruleInput?.account?.tradingName;
    },
    sortable: false,
    title: 'Account',
    width: '100px'
  },
  supplierId: {
    fixed: 'left',
    key: 'supplierId',
    render: (_, { ruleInput }): ReactNode => {
      const accountName: Optional<string> = ruleInput?.supplier?.tradingName;

      return accountName || '-';
    },
    sortable: false,
    title: 'Supplier',
    width: '100px'
  },
  collectionCountry: {
    key: 'collectionCountry',
    render: (_, { ruleInput }): ReactNode => {
      return renderArrayWithTooltip(
        'countries',
        ruleInput?.collectionCountry?.map((countryCode) => codeToCountryName(countryCode))
      );
    },
    sortable: false,
    title: 'Collection country'
  },
  countryOfDestination: {
    key: 'countryOfDestination',
    render: (_, { ruleInput }): ReactNode => {
      return codeToCountryName(ruleInput?.countryOfDestination) || '-';
    },
    sortable: false,
    title: 'Country of destination'
  },
  countryOfOrigin: {
    key: 'countryOfOrigin',
    render: (_, { ruleInput }): ReactNode => {
      return codeToCountryName(ruleInput?.countryOfOrigin) || '-';
    },
    sortable: false,
    title: 'Country of origin'
  },
  deliveryCountry: {
    key: 'deliveryCountry',
    render: (_, { ruleInput }): ReactNode => {
      return renderArrayWithTooltip(
        'countries',
        ruleInput?.deliveryCountry?.map((countryCode) => codeToCountryName(countryCode))
      );
    },
    sortable: false,
    title: 'Delivery country'
  },
  destination: {
    key: 'destination',
    render: (_, { ruleInput }): ReactNode => {
      return ruleInput?.destination || '-';
    },
    sortable: false,
    title: 'Destination'
  },
  eventId: {
    key: 'event',
    render: (_, { ruleInput }): ReactNode => {
      return renderArrayWithTooltip(
        'events',
        ruleInput?.event?.map((e) => e?.name || '')
      );
    },
    sortable: false,
    title: 'Events'
  },
  incoterms: {
    key: 'incoterms',
    render: (_, { ruleInput }): ReactNode => {
      return renderArrayWithTooltip('incoterms', ruleInput?.incoterms);
    },
    sortable: false,
    title: 'Incoterms'
  },
  modeOfTransport: {
    key: 'modeOfTransport',
    render: (_, { ruleInput }): ReactNode => {
      return ruleInput?.modeOfTransport || '-';
    },
    sortable: false,
    title: 'Mode of transport'
  },
  origin: {
    key: 'origin',
    render: (_, { ruleInput }): ReactNode => {
      return ruleInput?.origin || '-';
    },
    sortable: false,
    title: 'Origin'
  },
  shipmentContext: {
    key: 'shipmentContext',
    render: (_, { ruleInput }): ReactNode => {
      return ruleInput?.shipmentContext || '-';
    },
    sortable: false,
    title: 'Shipment context'
  },
  shipmentType: {
    key: 'shipmentType',
    render: (_, { ruleInput }): ReactNode => {
      return ruleInput?.shipmentType || '-';
    },
    sortable: false,
    title: 'Shipment type'
  },
  customerOperationsCoordinatorId: {
    className: 'bg-grey-lightest',
    key: 'customerOperationsCoordinatorId',
    render: (_, { ruleOutput }): ReactNode => {
      return (
        `${ruleOutput?.customerOperationsCoordinator?.firstName} ${ruleOutput?.customerOperationsCoordinator?.lastName}` || '-'
      );
    },
    sortable: false,
    title: 'Customer operations coordinator'
  },
  consigneeId: {
    key: 'consigneeId',
    render: (_, { ruleOutput }): ReactNode => {
      return ruleOutput.consignee?.label?.long || '-';
    },
    sortable: false,
    title: 'Consignee'
  },
  consignorId: {
    key: 'consignorId',
    render: (_, { ruleOutput }): ReactNode => {
      return ruleOutput.consignor?.label?.long || '-';
    },
    sortable: false,
    title: 'Consignor'
  },
  notifyPartyIds: {
    key: 'notifyPartyIds',
    render: (_, { ruleOutput }): ReactNode => {
      return renderArrayWithTooltip(
        'parties',
        ruleOutput?.notifyParties?.map((notifyParty) => notifyParty.label?.long || '')
      );
    },
    sortable: false,
    title: 'Notify Parties'
  },
  originAgentId: {
    className: 'bg-grey-lightest',
    key: 'originAgent',
    render: (_, { ruleOutput }): ReactNode => {
      return ruleOutput?.originAgent?.label?.long || '-';
    },
    sortable: false,
    title: 'Origin agent'
  },
  originOperationsCoordinatorId: {
    className: 'bg-grey-lightest',
    key: 'originOperationsCoordinatorId',
    render: (_, { ruleOutput }): ReactNode => {
      return `${ruleOutput?.originOperationsCoordinator?.firstName} ${ruleOutput?.originOperationsCoordinator?.lastName}` || '-';
    },
    sortable: false,
    title: 'Origin operations coordinator'
  },
  portOfDestination: {
    className: 'bg-grey-lightest',
    key: 'portOfDestination',
    render: (_, { ruleInput }): ReactNode => {
      return renderArrayWithTooltip('ports', ruleInput?.portOfDestination);
    },
    sortable: false,
    title: 'Port of destination'
  },
  portOfDestinationCountry: {
    key: 'portOfDestinationCountry',
    render: (_, { ruleInput }): ReactNode => {
      return renderArrayWithTooltip(
        'countries',
        ruleInput?.portOfDestinationCountry?.map((countryCode) => codeToCountryName(countryCode))
      );
    },
    sortable: false,
    title: 'Port of destination country'
  },
  portOfOrigin: {
    className: 'bg-grey-lightest',
    key: 'portOfOrigin',
    render: (_, { ruleInput }): ReactNode => {
      return renderArrayWithTooltip('ports', ruleInput?.portOfOrigin);
    },
    sortable: false,
    title: 'Port of origin'
  },
  portOfOriginCountry: {
    key: 'portOfOriginCountry',
    render: (_, { ruleInput }): ReactNode => {
      return renderArrayWithTooltip(
        'countries',
        ruleInput?.portOfOriginCountry?.map((countryCode) => codeToCountryName(countryCode))
      );
    },
    sortable: false,
    title: 'Port of origin country'
  },
  customsOperationsCoordinatorId: {
    className: 'bg-grey-lightest',
    key: 'customsOperationsCoordinatorId',
    render: (_, { ruleOutput }): ReactNode => {
      return (
        `${ruleOutput?.customsOperationsCoordinator?.firstName} ${ruleOutput?.customsOperationsCoordinator?.lastName}` || '-'
      );
    },
    sortable: false,
    title: 'Customs operations coordinator'
  },
  destinationOperationsCoordinatorId: {
    className: 'bg-grey-lightest',
    key: 'destinationOperationsCoordinatorId',
    render: (_, { ruleOutput }): ReactNode => {
      return (
        `${ruleOutput?.destinationOperationsCoordinator?.firstName} ${ruleOutput?.destinationOperationsCoordinator?.lastName}` ||
        '-'
      );
    },
    sortable: false,
    title: 'Destination operations coordinator'
  },
  supplierContactIds: {
    className: 'bg-grey-lightest',
    key: 'supplierContactIds',
    render: (_, { ruleOutput }): ReactNode => {
      return renderArrayWithTooltip(
        'contacts',
        ruleOutput?.supplierContacts?.map((supplierContact) => supplierContact.email)
      );
    },
    sortable: false,
    title: 'Supplier contacts'
  },
  actions: {
    className: 'bg-grey-lightest',
    key: 'actions',
    render: (_, { ruleOutput }): ReactNode => {
      return renderArrayWithTooltip(
        'actions',
        ruleOutput?.actions?.map((action) => action.label)
      );
    },
    sortable: false,
    title: 'Actions'
  }
};

export const getRuleColumns = (ruleFields: WorkflowRuleFieldTableItem[]): TableColumn<WorkflowRulesListItem>[] => {
  return ruleFields.map(({ name }: WorkflowRuleFieldTableItem) => {
    return workflowRuleTableColumns[name as keyof typeof workflowRuleTableColumns];
  });
};

export const renderArrayWithTooltip = (entityName: string, items?: Nullable<Nullable<string>[]>): React.ReactNode => {
  if (!items || items.length === 0) {
    return '-';
  }

  if (items.length > 1) {
    const tooltipContent = items.map((item) => (
      <div key={item} className="font-bold">
        {item}
      </div>
    ));

    return (
      <Tooltip placement="left" tooltipContent={tooltipContent}>
        {`${items.length} ${entityName}`}
      </Tooltip>
    );
  }

  return items.join(', ');
};
